import { onLocaleChange, t } from '../i18n';
import type { ViewModeStore } from './view-mode';
import type { ZoomState, ZoomStore } from './zoom-state';

export interface CanvasPair {
  source: HTMLCanvasElement;
  result: HTMLCanvasElement;
  overlay: HTMLCanvasElement;
  viewport: HTMLDivElement;
  stack: HTMLDivElement;
  frame: HTMLDivElement;
  frameLabel: HTMLSpanElement;
  dropzone: HTMLLabelElement;
  layoutEl: HTMLElement;
  /** Toggle between empty-state (dropzone visible, sidebar desaturated) and
   * loaded-state (canvas-stack visible, sidebar full color). main.ts calls
   * this once after `loadImage` parses + draws the source. */
  setHasImage(hasImage: boolean): void;
}

// Builds the redesigned viewport: grid background + four corner registration
// marks + a dropzone "card" empty state (brackets, halftone preview, big
// CLICK · DRAG & DROP · ⌘V text + meta sub-text) + a frame wrapping the
// canvas-stack with a violet RESULT/ORIGINAL label that fades on hover.
//
// `layoutEl` is the parent `<main class="layout">`; we set
// `data-empty="true"` while the frame is hidden so the CSS can desaturate
// the sidebar + zoom bar to focus attention on the dropzone.
export function createCanvasPair(
  parent: HTMLElement,
  zoomStore: ZoomStore,
  viewMode: ViewModeStore,
  layoutEl: HTMLElement,
): CanvasPair {
  const viewport = document.createElement('div');
  viewport.className = 'viewport';

  // Grid background (under everything)
  const gridBg = document.createElement('div');
  gridBg.className = 'grid-bg';
  gridBg.setAttribute('aria-hidden', 'true');
  viewport.appendChild(gridBg);

  // Registration marks
  for (const corner of ['tl', 'tr', 'bl', 'br'] as const) {
    const reg = document.createElement('span');
    reg.className = `reg ${corner}`;
    reg.setAttribute('aria-hidden', 'true');
    viewport.appendChild(reg);
  }

  // Canvas stack - frame around source + result canvases.
  const stack = document.createElement('div');
  stack.className = 'canvas-stack';
  stack.hidden = true;

  const frame = document.createElement('div');
  frame.className = 'frame';
  stack.appendChild(frame);

  const frameLabel = document.createElement('span');
  frameLabel.className = 'frame-label';
  frame.appendChild(frameLabel);

  const source = document.createElement('canvas');
  source.id = 'source-canvas';
  source.className = 'is-source';
  source.style.visibility = 'hidden';
  frame.appendChild(source);

  const result = document.createElement('canvas');
  result.id = 'result-canvas';
  result.className = 'is-result';
  frame.appendChild(result);

  const overlay = document.createElement('canvas');
  overlay.id = 'overlay-canvas';
  overlay.className = 'is-overlay';
  overlay.setAttribute('aria-hidden', 'true');
  frame.appendChild(overlay);

  viewport.appendChild(stack);

  // Dropzone (empty state) - the visible target is a real label for the
  // source file input so Safari opens the picker through native activation.
  // Drag-over styling lives on `.viewport[data-dragover='true']`.
  const dropzone = document.createElement('label');
  dropzone.htmlFor = 'sourceFileInput';
  dropzone.className = 'dropzone';
  dropzone.dataset['i18n'] = 'empty.headline';

  const card = document.createElement('div');
  card.className = 'dropzone-card';

  for (const corner of ['tl', 'tr', 'bl', 'br'] as const) {
    const bracket = document.createElement('span');
    bracket.className = `bracket ${corner}`;
    bracket.setAttribute('aria-hidden', 'true');
    card.appendChild(bracket);
  }

  const halftone = document.createElement('div');
  halftone.className = 'halftone-block';
  halftone.setAttribute('aria-hidden', 'true');
  const halftoneDots = document.createElement('div');
  halftoneDots.className = 'halftone-dots';
  halftone.appendChild(halftoneDots);
  const htTag = document.createElement('span');
  htTag.className = 'ht-tag tl';
  htTag.dataset['i18n'] = 'empty.preview';
  htTag.textContent = 'PREVIEW';
  halftone.appendChild(htTag);
  card.appendChild(halftone);

  const labelBlock = document.createElement('div');
  labelBlock.className = 'label-block';

  const big = document.createElement('div');
  big.className = 'big';
  const brkL = document.createElement('span');
  brkL.className = 'brk';
  brkL.textContent = '[';
  const bigText = document.createElement('span');
  bigText.dataset['i18n'] = 'empty.headline';
  const brkR = document.createElement('span');
  brkR.className = 'brk';
  brkR.textContent = ']';
  big.appendChild(brkL);
  big.appendChild(bigText);
  big.appendChild(brkR);
  labelBlock.appendChild(big);

  const meta = document.createElement('span');
  meta.className = 'meta';
  meta.dataset['i18n'] = 'empty.meta';
  labelBlock.appendChild(meta);

  card.appendChild(labelBlock);
  dropzone.appendChild(card);
  viewport.appendChild(dropzone);

  // Reflect view-mode (result vs source) on the frame label.
  function syncFrameLabel(): void {
    if (stack.hidden) return;
    const w = result.width || source.width || 0;
    const h = result.height || source.height || 0;
    const mode = viewMode.get();
    const labelKey = mode === 'source' ? 'view.source' : 'view.result';
    frameLabel.textContent =
      w > 0 && h > 0 ? `${t(labelKey)} · ${w} × ${h}` : t(labelKey);
  }

  function syncEmptyState(): void {
    bigText.textContent = t('empty.headline');
    meta.textContent = t('empty.meta');
    syncFrameLabel();
  }
  syncEmptyState();
  onLocaleChange(syncEmptyState);
  viewMode.subscribe(syncFrameLabel);

  // Drive `data-empty` on the layout while the dropzone is showing so
  // the CSS desaturates the sidebar + zoom bar.
  function setHasImage(hasImage: boolean): void {
    layoutEl.dataset['empty'] = hasImage ? 'false' : 'true';
    dropzone.hidden = hasImage;
    stack.hidden = !hasImage;
    if (hasImage) syncFrameLabel();
  }
  setHasImage(false);

  // Fade frame label whenever the user is interacting (pan/zoom/hover) -
  // the `:hover` rule covers most cases; `data-interacting` is set for
  // touch devices that don't fire :hover.
  let interactTimer: ReturnType<typeof setTimeout> | null = null;
  function bumpInteracting(): void {
    viewport.dataset['interacting'] = 'true';
    if (interactTimer) clearTimeout(interactTimer);
    interactTimer = setTimeout(() => {
      delete viewport.dataset['interacting'];
    }, 1500);
  }

  const applyTransform = (state: ZoomState): void => {
    stack.style.transform = `translate(${state.panX}px, ${state.panY}px) scale(${state.zoom})`;
    bumpInteracting();
    syncFrameLabel();
  };
  applyTransform(zoomStore.get());
  zoomStore.subscribe(applyTransform);

  viewport.addEventListener(
    'wheel',
    (event: WheelEvent) => {
      event.preventDefault();
      const rect = viewport.getBoundingClientRect();
      zoomStore.zoomToward(
        { x: event.clientX - rect.left, y: event.clientY - rect.top },
        event.deltaY < 0 ? 1.1 : 1 / 1.1,
      );
    },
    { passive: false },
  );

  const activePointers = new Map<number, { x: number; y: number }>();
  let prevPinchDist = 0;

  const shouldIgnoreViewportPointer = (event: PointerEvent): boolean => {
    return (
      event.target instanceof Element &&
      event.target.closest(
        'button, label, input, select, textarea, a, [role="button"]',
      ) !== null
    );
  };

  const localCoords = (event: PointerEvent): { x: number; y: number } => {
    const rect = viewport.getBoundingClientRect();
    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
  };

  viewport.addEventListener('pointerdown', (event: PointerEvent) => {
    if (shouldIgnoreViewportPointer(event)) return;
    viewport.setPointerCapture(event.pointerId);
    activePointers.set(event.pointerId, localCoords(event));
    viewport.dataset['panning'] = '1';
    if (activePointers.size === 2) {
      const points = Array.from(activePointers.values());
      prevPinchDist = Math.hypot(
        points[0]!.x - points[1]!.x,
        points[0]!.y - points[1]!.y,
      );
    }
  });

  viewport.addEventListener('pointermove', (event: PointerEvent) => {
    if (!activePointers.has(event.pointerId)) return;
    event.preventDefault();
    const previous = activePointers.get(event.pointerId)!;
    const next = localCoords(event);

    if (activePointers.size === 1) {
      zoomStore.pan(next.x - previous.x, next.y - previous.y);
      activePointers.set(event.pointerId, next);
      return;
    }

    if (activePointers.size === 2) {
      activePointers.set(event.pointerId, next);
      const points = Array.from(activePointers.values());
      const nextPinchDist = Math.hypot(
        points[0]!.x - points[1]!.x,
        points[0]!.y - points[1]!.y,
      );
      const anchor = {
        x: (points[0]!.x + points[1]!.x) / 2,
        y: (points[0]!.y + points[1]!.y) / 2,
      };
      zoomStore.zoomToward(anchor, nextPinchDist / Math.max(1, prevPinchDist));
      prevPinchDist = nextPinchDist;
    }
  });

  const releasePointer = (event: PointerEvent): void => {
    activePointers.delete(event.pointerId);
    if (viewport.hasPointerCapture(event.pointerId)) {
      viewport.releasePointerCapture(event.pointerId);
    }
    if (activePointers.size === 0) {
      delete viewport.dataset['panning'];
      prevPinchDist = 0;
    } else if (activePointers.size === 2) {
      const points = Array.from(activePointers.values());
      prevPinchDist = Math.hypot(
        points[0]!.x - points[1]!.x,
        points[0]!.y - points[1]!.y,
      );
    }
  };

  viewport.addEventListener('pointerup', releasePointer);
  viewport.addEventListener('pointercancel', releasePointer);

  const blockGesture = (event: Event): void => {
    event.preventDefault();
  };
  viewport.addEventListener('gesturestart', blockGesture as EventListener);
  viewport.addEventListener('gesturechange', blockGesture as EventListener);
  viewport.addEventListener('gestureend', blockGesture as EventListener);

  parent.appendChild(viewport);
  return {
    source,
    result,
    overlay,
    viewport,
    stack,
    frame,
    frameLabel,
    dropzone,
    layoutEl,
    setHasImage,
  };
}
